import type { JsxReconcilerStrategy } from './createJsxReconciler.ts';

/**
 * A node that holds its children in a mutable array. The array-children wiring
 * subset of a {@link JsxReconcilerStrategy} operates exclusively on this shape;
 * the `isArrayParent` guard narrows host nodes to it before any mutation.
 */
export type ArrayChildrenNode<NodeType> = { children: NodeType[] };

/** A text host node whose rendered string is mutated in place on commit. */
export type TextValueNode = { value: string };

/**
 * The subset of {@link JsxReconcilerStrategy} that wires children held in a
 * plain array: container append, ordered insert (`insertBefore` /
 * `insertInContainerBefore`), removal (`removeChild` /
 * `removeChildFromContainer`), container reset, and text-instance updates.
 *
 * `appendChild` / `appendInitialChild`, `createInstance` / `createTextInstance`,
 * and `commitUpdate` are intentionally excluded — those genuinely diverge
 * between renderers (e.g. the dashboard grid-item single-child slot, the
 * instructions-only `commitUpdate`) and stay as per-strategy handlers.
 */
type ArrayChildrenStrategy<
  NodeType,
  TextNodeType extends NodeType & TextValueNode,
  ContainerType extends ArrayChildrenNode<NodeType>,
> = Pick<
  JsxReconcilerStrategy<NodeType, TextNodeType, ContainerType>,
  | 'appendChildToContainer'
  | 'insertBefore'
  | 'insertInContainerBefore'
  | 'removeChild'
  | 'removeChildFromContainer'
  | 'clearContainer'
  | 'commitTextUpdate'
>;

const insertBeforeInArray = <NodeType>(children: NodeType[], child: NodeType, beforeChild: NodeType): void => {
  const idx = children.indexOf(beforeChild);
  if (idx >= 0) {
    children.splice(idx, 0, child);
  } else {
    children.push(child);
  }
};

const removeFromArray = <NodeType>(children: NodeType[], child: NodeType): void => {
  const idx = children.indexOf(child);
  if (idx >= 0) {
    children.splice(idx, 1);
  }
};

/**
 * Builds the array-children wiring subset of a {@link JsxReconcilerStrategy},
 * shared by every renderer whose parents store children in a plain array.
 *
 * Parent instances are narrowed through the supplied `isArrayParent` guard so
 * each renderer keeps its own node model (the dashboard grid-item slot, the
 * instructions text/fragment children) while reusing the identical
 * indexOf→splice insert/remove, container push, container reset, and
 * text-update logic. The host container is statically known to hold an array,
 * so container operations skip the runtime guard.
 *
 * Spread the result into a strategy and add the divergent handlers
 * (`createInstance`, `appendChild` / `appendInitialChild`, `commitUpdate`).
 */
export function arrayChildrenStrategy<
  NodeType,
  TextNodeType extends NodeType & TextValueNode,
  ContainerType extends ArrayChildrenNode<NodeType>,
>(
  isArrayParent: (node: NodeType) => node is NodeType & ArrayChildrenNode<NodeType>,
): ArrayChildrenStrategy<NodeType, TextNodeType, ContainerType> {
  return {
    appendChildToContainer: (container, child) => {
      container.children.push(child);
    },

    insertBefore: (parent, child, beforeChild) => {
      if (isArrayParent(parent)) {
        insertBeforeInArray(parent.children, child, beforeChild);
      }
    },
    insertInContainerBefore: (container, child, beforeChild) => {
      insertBeforeInArray(container.children, child, beforeChild);
    },

    removeChild: (parent, child) => {
      if (isArrayParent(parent)) {
        removeFromArray(parent.children, child);
      }
    },
    removeChildFromContainer: (container, child) => {
      removeFromArray(container.children, child);
    },

    clearContainer: (container) => {
      container.children = [];
    },

    commitTextUpdate: (textInstance, _oldText, newText) => {
      textInstance.value = newText;
    },
  };
}
